import React, { useState, useRef } from 'react';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import MurphyInteractionWarning from '../Murphy/MurphyInteractionWarning';

const NewAssessmentDetails = ({ assessments, onSaveToLibrary, canEditLibrary = true }) => {
    const [selectedAssessmentId, setSelectedAssessmentId] = useState(assessments[0]?.id);
    const [selectedSessionType, setSelectedSessionType] = useState(assessments[0]?.sessionType);
    const [savedQuestions, setSavedQuestions] = useState({});
    const questionBreakdownRef = useRef(null);
    const dateOptions = assessments.filter(assessment => assessment.sessionType === selectedSessionType).map((assessment, index) => ({
        key: index,
        text: new Date(assessment.completedDateTime).toLocaleDateString(),
        value: assessment.id,
    }));

    const sessionTypeOptions = assessments.reduce((collector, assessment, index) => {
        if (!collector.some(option => option.value === assessment.sessionType)) {
            collector.push({
                key: index,
                text: assessment.sessionType,
                value: assessment.sessionType
            })
        }
        return collector;
    }, [])

    const handleSessionTypeChange = (e) => {
        const value = e.target ? e.target.value : e;
        setSelectedSessionType(value);
        const firstAssessment = assessments.filter(assessment => assessment.sessionType === value)[0];
        setSelectedAssessmentId(firstAssessment?.id);
    }

    const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId);

    if (!selectedAssessment) {
        return <div style={{ margin: 20, fontWeight: 'bold' }}>No assessments completed.</div>;
    }
    return (
        <>
            {selectedAssessment.aiGenerated && (
                <div
                    style={{
                        backgroundColor: 'rgb(7, 132, 167)',
                        padding: '16px',
                        textAlign: 'center',
                    }}
                >
                    <div style={{ color: 'white', margin: 0, fontFamily: '"Press Start 2P"', fontSize: '9px' }}>
                        &#x1F575;&#xFE0F; Review created by Murphy the MultiplyAI agent
                    </div>
                </div>
            )}
            <div style={{ padding: '20px', fontFamily: 'Montserrat, sans-serif' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '5px' }}>Type of Sales Message:</label>
                        <select
                            value={selectedSessionType}
                            onChange={handleSessionTypeChange}
                            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', minWidth: '180px' }}
                        >
                            {sessionTypeOptions.map(opt => (
                                <option key={opt.key} value={opt.value}>{opt.text}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '5px' }}>Date of Assessment:</label>
                        <select
                            value={selectedAssessmentId}
                            onChange={(e) => setSelectedAssessmentId(e.target.value)}
                            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', minWidth: '150px' }}
                        >
                            {dateOptions.map(opt => (
                                <option key={opt.key} value={opt.value}>{opt.text}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px' }}>
                        <div
                            data-testid="assessment-grade-1"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                backgroundColor: { green: '#21ba45', yellow: '#fbbd08', red: '#db2828', grey: '#767676' }[selectedAssessment.grade] || '#767676',
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                            Attempts: <strong>{selectedAssessment.numberOfTries}</strong>
                        </span>
                        <VideoPlayer videoKey={selectedAssessment.videoName} />
                        {selectedAssessment.sessionType === 'Prospect Prep' && selectedAssessment.qaAnalysis?.questionAnalysis?.length > 0 && (
                            <button
                                onClick={() => questionBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                style={{
                                    fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                                    padding: '6px 14px', borderRadius: '6px', border: '1px solid #0784a7',
                                    backgroundColor: '#0784a7', color: '#fff', cursor: 'pointer',
                                    marginLeft: '8px', marginTop: '10px', height: '32px', whiteSpace: 'nowrap',
                                }}
                            >
                                Jump to Question Breakdown
                            </button>
                        )}
                    </div>
                </div>
                <MurphyInteractionWarning assessment={selectedAssessment} />
                <div style={{ display: 'flex', gap: '24px' }}>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Positives</h3>
                        {selectedAssessment.positivesView.map((positive, i) =>
                            <div key={`positive_${i}`} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '12px 16px', marginBottom: '8px',
                                background: '#f0fdf4', borderLeft: '4px solid #21ba45',
                                borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#1e293b',
                            }}>
                                <span style={{ color: '#21ba45', flexShrink: 0, marginTop: '2px' }}>&#10003;</span>
                                <span>{positive}</span>
                            </div>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Areas For Improvement</h3>
                        {selectedAssessment.areasForImprovementView.map((negative, i) =>
                            <div key={`negative${i}`} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '12px 16px', marginBottom: '8px',
                                background: '#fef2f2', borderLeft: '4px solid #db2828',
                                borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#1e293b',
                            }}>
                                <span style={{ color: '#db2828', flexShrink: 0, marginTop: '2px' }}>&#9888;</span>
                                <span>{negative}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Per-question feedback — Prospect Prep only */}
                {selectedAssessment.sessionType === 'Prospect Prep' && selectedAssessment.qaAnalysis?.questionAnalysis?.length > 0 && (
                    <div ref={questionBreakdownRef} style={{ marginTop: '32px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>
                            Question Breakdown
                        </h3>

                        {selectedAssessment.qaAnalysis.questionAnalysis.map((qa, i) => {
                            const key = `${selectedAssessment.id}_${i}`;
                            const isSaved = savedQuestions[key] === 'saved';
                            const isSaving = savedQuestions[key] === 'saving';

                            return (
                                <div key={`qa_detail_${i}`} style={{
                                    padding: '16px', marginBottom: '12px',
                                    background: '#f8fafc', border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>
                                        Q{i + 1}: {qa.question}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', marginBottom: '8px' }}>
                                        <strong>Expected:</strong> {qa.expectedAnswer}
                                    </div>
                                    {qa.keyPointsCovered?.length > 0 && (
                                        <div style={{ fontSize: '13px', color: '#16a34a', marginBottom: '4px' }}>
                                            &#10003; Covered: {qa.keyPointsCovered.join(', ')}
                                        </div>
                                    )}
                                    {qa.keyPointsMissed?.length > 0 && (
                                        <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '4px' }}>
                                            &#9888; Missed: {qa.keyPointsMissed.join(', ')}
                                        </div>
                                    )}
                                    {qa.improvementSuggestion && (
                                        <div style={{ fontSize: '13px', color: '#6366f1', marginBottom: '8px' }}>
                                            &#128161; {qa.improvementSuggestion}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: qa.pointsAwarded / qa.maxPoints >= 0.65 ? '#16a34a' : qa.pointsAwarded / qa.maxPoints >= 0.45 ? '#ca8a04' : '#dc2626' }}>
                                            {qa.pointsAwarded}/{qa.maxPoints} points
                                        </span>
                                        {onSaveToLibrary && canEditLibrary && (
                                            isSaved ? (
                                                <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>
                                                    &#10003; Added to Objection Handling
                                                </span>
                                            ) : (
                                                <button
                                                    disabled={isSaving}
                                                    onClick={async () => {
                                                        setSavedQuestions(prev => ({ ...prev, [key]: 'saving' }));
                                                        try {
                                                            await onSaveToLibrary([{
                                                                question: qa.question,
                                                                answer: qa.expectedAnswer,
                                                                related_to: 'Prospect Prep'
                                                            }]);
                                                            setSavedQuestions(prev => ({ ...prev, [key]: 'saved' }));
                                                        } catch (err) {
                                                            console.error('Failed to save question:', err);
                                                            setSavedQuestions(prev => ({ ...prev, [key]: undefined }));
                                                        }
                                                    }}
                                                    style={{
                                                        fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600,
                                                        padding: '6px 14px', borderRadius: '4px', border: '1px solid #0784a7',
                                                        backgroundColor: 'transparent', color: '#0784a7',
                                                        cursor: isSaving ? 'default' : 'pointer',
                                                    }}
                                                >
                                                    {isSaving ? 'Adding...' : 'Add to Objection Handling'}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default NewAssessmentDetails;